import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import {
  Card, Button, Badge, SearchInput, Select, Spinner, EmptyState,
  Table, TableHead, TableBody, TableRow, TableCell, Modal
} from '../../components/UI';
import { usersAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlinePlus, HiOutlineUsers, HiOutlinePhone,
  HiOutlineMail, HiOutlinePencil, HiOutlineTrash, HiOutlineDownload,
  HiOutlineCheck, HiOutlineX
} from 'react-icons/hi';
import { format } from 'date-fns';
import UserModal from './UserModal';

const roleOptions = [
  { value: '', label: 'All Roles' },
  { value: 'admin', label: 'Admin' },
  { value: 'telecaller', label: 'Telecaller' },
];

const UsersList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);

  // Fetch users
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', roleFilter],
    queryFn: async () => {
      const params = {};
      if (roleFilter) params.role = roleFilter;
      const response = await usersAPI.getAll(params);
      return response.data?.data || [];
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => usersAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      toast.success('User deleted successfully');
      setShowDeleteModal(false);
      setUserToDelete(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    },
  });

  // Bulk update status mutation
  const bulkUpdateStatusMutation = useMutation({
    mutationFn: ({ ids, isActive }) => usersAPI.bulkUpdate({ ids, isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      toast.success('User status updated successfully');
      setSelectedUsers([]);
      setShowBulkStatusModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update user status');
    },
  });

  const users = usersData || [];

  const filteredUsers = search
    ? users.filter(u =>
        u.firstName?.toLowerCase().includes(search.toLowerCase()) ||
        u.lastName?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  // Selection handlers
  const handleSelectUser = (id) => {
    setSelectedUsers(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u._id));
    }
  };

  const handleClearSelection = () => {
    setSelectedUsers([]);
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      deleteMutation.mutate(userToDelete._id);
    }
  };

  // Export users to CSV
  const handleExportUsers = () => {
    try {
      if (filteredUsers.length === 0) {
        toast.error('No users to export');
        return;
      }

      // Create CSV headers
      const headers = ['Name', 'Email', 'Role', 'Primary Phone', 'All Phones', 'Department', 'Status', 'Created At'];

      // Create CSV rows
      const csvRows = [
        headers.join(','),
        ...filteredUsers.map(user => {
          const phones = user.phones || [];
          const primaryPhone = phones.find(p => p.isDefault)?.number || phones[0]?.number || '';
          const allPhones = phones.map(p => `${p.number} (${p.label})`).join('; ');

          return [
            `"${user.firstName || ''} ${user.lastName || ''}"`.trim(),
            `"${user.email || ''}"`,
            `"${user.role || ''}"`,
            `"${primaryPhone}"`,
            `"${allPhones}"`,
            `"${user.department || ''}"`,
            `"${user.isActive !== false ? 'Active' : 'Inactive'}"`,
            `"${user.createdAt ? new Date(user.createdAt).toLocaleDateString() : ''}"`
          ].join(',');
        })
      ];

      // Add summary section
      csvRows.unshift('');
      csvRows.unshift(`Total Users: ${filteredUsers.length}`);
      csvRows.unshift(`Export Date: ${new Date().toLocaleString()}`);
      csvRows.unshift('');
      csvRows.unshift('=== USERS EXPORT ===');
      csvRows.unshift('');

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `users-export-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast.success(`Exported ${filteredUsers.length} users successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export users');
    }
  };

  // Helper function to get display phone
  const getDisplayPhone = (user) => {
    const phones = user.phones || [];
    if (phones.length === 0) return '-';
    const defaultPhone = phones.find(p => p.isDefault) || phones[0];
    return defaultPhone.number;
  };

  // Helper function to get all phones count
  const getPhonesCount = (user) => {
    return (user.phones || []).filter(p => p.number).length;
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin':
      default:
        return 'primary';
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Users</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">Manage telecallers and staff</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineDownload className="w-4 h-4" />}
              onClick={handleExportUsers}
            >
              <span className="hidden sm:inline">Export</span>
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<HiOutlinePlus className="w-4 h-4" />}
              onClick={handleAddUser}
            >
              <span className="hidden sm:inline">Add User</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card padding="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="flex-1">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search users..."
              />
            </div>
            <Select
              options={roleOptions}
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="sm:w-40"
            />
          </div>
        </Card>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-primary-50 rounded-xl animate-fadeIn">
            <span className="text-xs sm:text-sm font-medium text-primary-700">
              {selectedUsers.length} selected
            </span>
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineCheck className="w-4 h-4" />}
              onClick={() => setShowBulkStatusModal(true)}
            >
              <span className="hidden sm:inline">Activate</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineX className="w-4 h-4" />}
              onClick={() => {
                bulkUpdateStatusMutation.mutate({ ids: selectedUsers, isActive: false });
              }}
              disabled={bulkUpdateStatusMutation.isPending}
            >
              <span className="hidden sm:inline">Deactivate</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSelection}
            >
              Clear
            </Button>
          </div>
        )}

        {/* Users List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlineUsers className="w-8 h-8 text-gray-400" />}
              title="No users found"
              description="Add your first user to get started"
            />
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableCell header>
                      <input
                        type="checkbox"
                        checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600"
                      />
                    </TableCell>
                    <TableCell header>User</TableCell>
                    <TableCell header>Email</TableCell>
                    <TableCell header>Role</TableCell>
                    <TableCell header>Phone</TableCell>
                    <TableCell header>Status</TableCell>
                    <TableCell header>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user._id}>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user._id)}
                          onChange={() => handleSelectUser(user._id)}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 cursor-pointer"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-primary-600 font-medium">
                              {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </p>
                            {user.department && (
                              <p className="text-xs text-gray-500">{user.department}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600">{user.email}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.role === 'admin' ? 'primary' : user.role === 'manager' ? 'info' : 'default'} size="sm">
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-gray-600">
                            {getDisplayPhone(user)}
                          </span>
                          {getPhonesCount(user) > 1 && (
                            <span className="text-xs text-gray-400">
                              +{getPhonesCount(user) - 1} more
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.isActive !== false ? 'success' : 'danger'} size="sm">
                          {user.isActive !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<HiOutlinePencil className="w-4 h-4" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditUser(user);
                            }}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(user);
                            }}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filteredUsers.map((user) => (
                <div
                  key={user._id}
                  className="p-3 sm:p-4 hover:bg-gray-50 transition-colors"
                >
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user._id)}
                        onChange={() => handleSelectUser(user._id)}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 mt-0.5 cursor-pointer flex-shrink-0"
                      />
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary-600 font-medium text-sm">
                          {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-500 truncate">{user.email}</p>
                        {user.department && (
                          <p className="text-xs text-gray-400 truncate">{user.department}</p>
                        )}
                      </div>
                    </div>
                    <Badge variant={getRoleBadgeVariant(user.role)} size="sm">
                      {user.role}
                    </Badge>
                  </div>

                  {/* Info Row */}
                  <div className="flex items-center justify-between text-xs sm:text-sm mb-3 pl-11 sm:pl-12">
                    <div className="flex items-center gap-3 text-gray-500">
                      {(user.phones || []).length > 0 && (
                        <div className="flex items-center gap-1">
                          <HiOutlinePhone className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          <span className="truncate">{getDisplayPhone(user)}</span>
                          {getPhonesCount(user) > 1 && (
                            <span className="text-xs text-gray-400">+{getPhonesCount(user) - 1}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <Badge variant={user.isActive !== false ? 'success' : 'danger'} size="sm">
                      {user.isActive !== false ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>

                  {/* Actions Row */}
                  <div className="flex items-center justify-end gap-1 pt-2 border-t border-gray-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2"
                      icon={<HiOutlinePencil className="w-4 h-4 text-gray-500" />}
                      onClick={() => handleEditUser(user)}
                      title="Edit"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2"
                      icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                      onClick={() => handleDeleteClick(user)}
                      title="Delete"
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* User Modal */}
        <UserModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          onSuccess={() => queryClient.invalidateQueries(['users'])}
        />

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete User"
          size="sm"
        >
          <div className="space-y-3 sm:space-y-4">
            <p className="text-gray-600 text-sm">
              Are you sure you want to delete <span className="font-semibold">{userToDelete?.firstName} {userToDelete?.lastName}</span>? This action cannot be undone.
            </p>
            <div className="flex gap-2 sm:gap-3 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleConfirmDelete}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>

        {/* Bulk Status Modal */}
        <Modal
          isOpen={showBulkStatusModal}
          onClose={() => setShowBulkStatusModal(false)}
          title="Activate Users"
          size="sm"
        >
          <div className="space-y-3 sm:space-y-4">
            <p className="text-gray-600 text-sm">
              Activate <span className="font-semibold">{selectedUsers.length}</span> selected users?
            </p>
            <div className="flex gap-2 sm:gap-3 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowBulkStatusModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => bulkUpdateStatusMutation.mutate({ ids: selectedUsers, isActive: true })}
                loading={bulkUpdateStatusMutation.isPending}
              >
                Activate
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export default UsersList;